import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { 
  Plus, 
  Save, 
  ArrowLeft, 
  GripVertical,
  Trash2,
  Settings,
  Eye
} from 'lucide-react';
import { v4 as uuidv4 } from 'uuid';
import { templatesApi } from '../services/api';
import { Question, Template } from '../types';
import toast from 'react-hot-toast';

const QUESTION_TYPES = [
  { value: 'text', label: 'Text', icon: 'T' },
  { value: 'number', label: 'Number', icon: '123' },
  { value: 'yes_no', label: 'Yes/No', icon: '✓' },
  { value: 'multiple_choice', label: 'Multiple Choice', icon: '☰' },
  { value: 'photo', label: 'Photo', icon: '📷' },
  { value: 'signature', label: 'Signature', icon: '✍' },
  { value: 'date', label: 'Date', icon: '📅' },
  { value: 'rating', label: 'Rating', icon: '★' }
];

const TemplateBuilder: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const queryClient = useQueryClient();
  const isEditing = Boolean(id);

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('');
  const [industry, setIndustry] = useState('');
  const [isPublic, setIsPublic] = useState(false);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [showPreview, setShowPreview] = useState(false);

  const { data: existingTemplate } = useQuery(
    ['template', id],
    () => templatesApi.getById(id!).then(r => r.data),
    { enabled: isEditing }
  );

  useEffect(() => {
    if (existingTemplate) {
      setTitle(existingTemplate.title);
      setDescription(existingTemplate.description || '');
      setCategory(existingTemplate.category || '');
      setIndustry(existingTemplate.industry || '');
      setIsPublic(existingTemplate.isPublic);
      setQuestions(existingTemplate.questions || []);
    }
  }, [existingTemplate]);

  const saveMutation = useMutation(
    (data: any) => isEditing 
      ? templatesApi.update(id!, data)
      : templatesApi.create(data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('templates');
        toast.success(isEditing ? 'Template updated' : 'Template created');
        navigate('/templates');
      },
      onError: () => toast.error('Failed to save template')
    }
  );

  const addQuestion = (type: string) => {
    const newQuestion: Question = {
      id: uuidv4(),
      type: type as any,
      text: `New ${QUESTION_TYPES.find(t => t.value === type)?.label} Question`,
      required: false,
      options: type === 'multiple_choice' ? ['Option 1', 'Option 2'] : undefined
    };
    setQuestions([...questions, newQuestion]);
  };

  const updateQuestion = (index: number, updates: Partial<Question>) => {
    const updated = [...questions];
    updated[index] = { ...updated[index], ...updates };
    setQuestions(updated);
  };

  const deleteQuestion = (index: number) => {
    setQuestions(questions.filter((_, i) => i !== index));
  };

  const handleDragEnd = (result: any) => {
    if (!result.destination) return;
    
    const items = Array.from(questions);
    const [reordered] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reordered);
    setQuestions(items);
  };

  const handleSave = () => {
    if (!title.trim()) {
      toast.error('Please enter a template title');
      return;
    }
    if (questions.length === 0) {
      toast.error('Add at least one question');
      return;
    }

    saveMutation.mutate({
      title,
      description,
      category,
      industry,
      isPublic,
      questions,
      scoring: { enabled: true }
    });
  };

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/templates')}
            className="p-2 hover:bg-gray-100 rounded-lg"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {isEditing ? 'Edit Template' : 'Create Template'}
            </h1>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setShowPreview(!showPreview)}
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            <Eye className="w-5 h-5" />
            {showPreview ? 'Edit' : 'Preview'}
          </button>
          <button
            onClick={handleSave}
            disabled={saveMutation.isLoading}
            className="flex items-center gap-2 bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 disabled:opacity-50"
          >
            <Save className="w-5 h-5" />
            {saveMutation.isLoading ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>

      <div className="flex-1 grid grid-cols-1 lg:grid-cols-3 gap-6 min-h-0">
        {/* Sidebar - Question Types */}
        <div className={`bg-white rounded-xl shadow-sm p-4 ${showPreview ? 'hidden' : ''}`}>
          <h3 className="font-semibold text-gray-900 mb-4">Add Questions</h3>
          <div className="space-y-2">
            {QUESTION_TYPES.map((type) => (
              <button
                key={type.value}
                onClick={() => addQuestion(type.value)}
                className="flex items-center gap-3 w-full p-3 rounded-lg border border-gray-200 hover:border-primary-500 hover:bg-primary-50 transition-colors text-left"
              >
                <span className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center text-sm font-medium">
                  {type.icon}
                </span>
                <span className="font-medium">{type.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Main Content */}
        <div className={`${showPreview ? 'lg:col-span-3' : 'lg:col-span-2'} space-y-6 overflow-auto`}>
          {/* Template Settings */}
          {!showPreview && (
            <div className="bg-white rounded-xl shadow-sm p-6">
              <div className="flex items-center gap-2 mb-4">
                <Settings className="w-5 h-5 text-gray-400" />
                <h3 className="font-semibold text-gray-900">Template Settings</h3>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Title *</label>
                  <input
                    type="text"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 outline-none"
                    placeholder="Enter template title"
                  />
                </div>
                
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                  <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 outline-none"
                    rows={2}
                    placeholder="Describe the purpose of this template"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
                  <input
                    type="text"
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 outline-none"
                    placeholder="e.g., Safety, Quality"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Industry</label>
                  <input
                    type="text"
                    value={industry}
                    onChange={(e) => setIndustry(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 outline-none"
                    placeholder="e.g., Construction, Manufacturing"
                  />
                </div>
              </div>
              
              <div className="mt-4 flex items-center gap-2">
                <input
                  type="checkbox"
                  id="isPublic"
                  checked={isPublic}
                  onChange={(e) => setIsPublic(e.target.checked)}
                  className="w-4 h-4 text-primary-600 rounded"
                />
                <label htmlFor="isPublic" className="text-sm text-gray-700">
                  Make this template public (visible to all organizations)
                </label>
              </div>
            </div>
          )}

          {/* Questions List */}
          {showPreview ? (
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-6">{title || 'Untitled Template'}</h2>
              <div className="space-y-6">
                {questions.map((q, i) => (
                  <div key={q.id} className="border-b pb-6">
                    <p className="font-medium text-gray-900">
                      {i + 1}. {q.text}
                      {q.required && <span className="text-red-500"> *</span>}
                    </p>
                    {q.description && <p className="text-sm text-gray-600 mt-1">{q.description}</p>}
                    <div className="mt-3">
                      {q.type === 'text' && (
                        <input type="text" disabled className="w-full px-4 py-2 border rounded-lg bg-gray-50" placeholder="Text answer..." />
                      )}
                      {q.type === 'yes_no' && (
                        <div className="flex gap-4">
                          <label className="flex items-center gap-2"><input type="radio" disabled /> Yes</label>
                          <label className="flex items-center gap-2"><input type="radio" disabled /> No</label>
                        </div>
                      )}
                      {q.type === 'multiple_choice' && (
                        <div className="space-y-2">
                          {q.options?.map((opt, j) => (
                            <label key={j} className="flex items-center gap-2">
                              <input type="checkbox" disabled /> {opt}
                            </label>
                          ))}
                        </div>
                      )}
                      {q.type === 'photo' && (
                        <div className="w-full h-32 border-2 border-dashed rounded-lg flex items-center justify-center text-gray-400">
                          Photo upload area
                        </div>
                      )}
                      {q.type === 'signature' && (
                        <div className="w-full h-32 border-2 border-dashed rounded-lg flex items-center justify-center text-gray-400">
                          Signature area
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <DragDropContext onDragEnd={handleDragEnd}>
              <Droppable droppableId="questions">
                {(provided) => (
                  <div {...provided.droppableProps} ref={provided.innerRef} className="space-y-4">
                    {questions.map((question, index) => (
                      <Draggable key={question.id} draggableId={question.id} index={index}>
                        {(provided, snapshot) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            className={`bg-white rounded-xl shadow-sm p-4 ${snapshot.isDragging ? 'shadow-lg' : ''}`}
                          >
                            <div className="flex items-start gap-4">
                              <div {...provided.dragHandleProps} className="mt-2 cursor-grab">
                                <GripVertical className="w-5 h-5 text-gray-400" />
                              </div>
                              
                              <div className="flex-1 space-y-4">
                                <div className="flex items-center gap-2">
                                  <span className="text-xs font-medium text-gray-500 uppercase">
                                    {QUESTION_TYPES.find(t => t.value === question.type)?.label}
                                  </span>
                                </div>
                                
                                <input
                                  type="text"
                                  value={question.text}
                                  onChange={(e) => updateQuestion(index, { text: e.target.value })}
                                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 outline-none font-medium"
                                  placeholder="Question text"
                                />
                                
                                <input
                                  type="text"
                                  value={question.description || ''}
                                  onChange={(e) => updateQuestion(index, { description: e.target.value })}
                                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 outline-none text-sm"
                                  placeholder="Description (optional)"
                                />
                                
                                {question.type === 'multiple_choice' && (
                                  <div className="space-y-2">
                                    <p className="text-sm font-medium text-gray-700">Options:</p>
                                    {question.options?.map((opt, optIndex) => (
                                      <div key={optIndex} className="flex items-center gap-2">
                                        <input
                                          type="text"
                                          value={opt}
                                          onChange={(e) => {
                                            const newOptions = [...(question.options || [])];
                                            newOptions[optIndex] = e.target.value;
                                            updateQuestion(index, { options: newOptions });
                                          }}
                                          className="flex-1 px-3 py-1 border border-gray-300 rounded text-sm"
                                        />
                                        <button
                                          onClick={() => {
                                            const newOptions = question.options?.filter((_, i) => i !== optIndex);
                                            updateQuestion(index, { options: newOptions });
                                          }}
                                          className="text-red-500 hover:text-red-700"
                                        >
                                          <Trash2 className="w-4 h-4" />
                                        </button>
                                      </div>
                                    ))}
                                    <button
                                      onClick={() => updateQuestion(index, { 
                                        options: [...(question.options || []), `Option ${(question.options?.length || 0) + 1}`] 
                                      })}
                                      className="text-sm text-primary-600 hover:text-primary-700"
                                    >
                                      + Add option
                                    </button>
                                  </div>
                                )}
                                
                                <div className="flex items-center gap-4">
                                  <label className="flex items-center gap-2 text-sm">
                                    <input
                                      type="checkbox"
                                      checked={question.required}
                                      onChange={(e) => updateQuestion(index, { required: e.target.checked })}
                                      className="rounded"
                                    />
                                    Required
                                  </label>
                                </div>
                              </div>
                              
                              <button
                                onClick={() => deleteQuestion(index)}
                                className="p-2 text-gray-400 hover:text-red-500 transition-colors"
                              >
                                <Trash2 className="w-5 h-5" />
                              </button>
                            </div>
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          )}
        </div>
      </div>
    </div>
  );
};

export default TemplateBuilder;